import React, { useEffect, useId, useRef, useState } from "react";
import "./pieceStory.css";
import { HKPImage } from "../../../media/HKPImage";
import { PublicProductionSummary } from "../../karkhana/sarees/ProductionHistory";

type Props = {
  form: any;
  /** Line above the steps, e.g. "Making story recorded on HKP." */
  attribution: string;
  /** A reseller's client: no brand row and no studio loom record. */
  hideBrand?: boolean;
  /**
   * Called the first time the buyer opens the story for this piece. The story starts closed so an
   * open is a real signal of interest the studio can see.
   */
  onOpen?: () => void;
};

type Spec = { label: string; value: string };

const clean = (v: unknown): string => {
  if (v == null || typeof v === "object") return "";
  const s = String(v).trim();
  return s === "undefined" || s === "null" || s === "N/A" ? "" : s;
};

const withUnit = (v: unknown, unit: string) => (clean(v) ? `${clean(v)} ${unit}` : "");

/**
 * Every data point the product certificate shows, only when filled: an empty row tells a buyer
 * nothing and pushes the story off screen.
 */
export function filledSpecs(form: any, hideBrand = false): Spec[] {
  const rows: Spec[] = [
    { label: "Artform", value: clean(form?.artform) },
    { label: "Type", value: clean(form?.type) },
    { label: "Category", value: clean(form?.category) },
    { label: "Sub-category", value: clean(form?.subCategory) },
    { label: "Weave", value: clean(form?.weaveType) },
    { label: "Colour", value: clean(form?.color) },
    { label: "Design", value: clean(form?.typeOfSareeByDesign) },
    { label: "Weaving centre", value: clean(form?.weavingCenter) },
    { label: "Weaver", value: clean(form?.nameOfWeaver) },
    { label: "Loom", value: clean(form?.loomNum) },
    { label: "Zari", value: clean(form?.zariUsed?.label) },
    { label: "Zari purity", value: clean(form?.purity) },
    { label: "Weight", value: withUnit(form?.weightOfSaree, "g") },
    { label: "Zari weight", value: withUnit(form?.weightOfZariUsed, "g") },
    { label: "Yarn", value: clean(form?.yarnType) },
    {
      label: "Time taken",
      value: clean(form?.productionTimeInMonths)
        ? `${clean(form?.productionTimeInMonths)} month${clean(form?.productionTimeInMonths) === "1" ? "" : "s"}`
        : "",
    },
    { label: "Working hours", value: withUnit(form?.workingHours, "hrs") },
    { label: "Design no.", value: clean(form?.designNum) },
    { label: "Made in", value: form?.madeInIndia ? "India" : "" },
  ];
  if (!hideBrand) rows.push({ label: "Brand", value: clean(form?.nameOfBrand) });
  return rows.filter((r) => r.value);
}

type StoryStep = {
  key: string;
  title: string;
  subtitle: string;
  text: string;
  artisan: string;
  artisanBio: string;
  artisanPhoto: string;
  media: string[];
};

const mediaUrl = (m: any) => clean(m?.mediaURL || m?.mediaUrl || m?.blobImage);
const shown = (m: any) => m?.show !== "0" && m?.show !== 0 && m?.show !== false;

/** Matching key for process names: "Dying", "dyeing" and "Dyeing process" are one step. */
const processKey = (title: string) => {
  const t = title.toLowerCase().replace(/process/g, "").trim();
  if (/^dy(e)?ing|^dye/.test(t)) return "dyeing";
  if (/^weav/.test(t)) return "weaving";
  if (/^design/.test(t)) return "designing";
  if (/^finish/.test(t)) return "finishing";
  return t;
};

/** Legacy records spell it "Dying"; buyers read "Dyeing". */
const displayTitle = (title: string) => (processKey(title) === "dyeing" ? "Dyeing" : title);

/**
 * Certificate processes (designing, dyeing, weaving, finishing or flexible) with their artisans and
 * photos. A free-text step with the same name adds its words to that process instead of repeating it.
 */
export function storySteps(form: any): StoryStep[] {
  const fromCertificate: StoryStep[] = (Array.isArray(form?.certificateProcesses) ? form.certificateProcesses : [])
    .map((p: any, i: number) => ({
      key: `c-${p?.key || i}`,
      title: displayTitle(clean(p?.title)),
      subtitle: clean(p?.subtitle),
      text: clean(p?.text),
      artisan: clean(p?.karigar?.name),
      artisanBio: clean(p?.karigar?.description),
      artisanPhoto: clean(p?.karigar?.profileURL),
      media: (Array.isArray(p?.media) ? p.media : []).filter(shown).map(mediaUrl).filter(Boolean),
    }))
    .filter((s: StoryStep) => s.subtitle || s.text || s.artisan || s.media.length);
  const out = [...fromCertificate];
  (Array.isArray(form?.processSteps) ? form.processSteps : []).forEach((raw: any, i: number) => {
    const title = clean(raw?.title);
    const text = clean(raw?.text);
    if (!title && !text) return;
    const match = title ? out.find((s) => s.title && processKey(s.title) === processKey(title)) : undefined;
    if (match) {
      match.text = match.text ? `${match.text}\n${text}` : text;
      return;
    }
    out.push({
      key: `s-${i}`,
      title: displayTitle(title),
      subtitle: "",
      text,
      artisan: "",
      artisanBio: "",
      artisanPhoto: "",
      media: [],
    });
  });
  return out;
}

/**
 * The making story first (it is what proves the piece is handmade), then the specs that exist.
 * Replaces the tabbed table on shared catalogue pages; the studio product page keeps its own view.
 */
const PieceStory: React.FC<Props> = ({ form, attribution, hideBrand, onOpen }) => {
  const weaver = clean(form?.nameOfWeaver);
  const loom = clean(form?.loomNum);
  const months = clean(form?.productionTimeInMonths);
  const hours = clean(form?.workingHours);
  const steps = storySteps(form);
  const specs = filledSpecs(form, hideBrand);
  const marks = [
    form?.handloom || form?.handloomMark === true ? "Handloom mark" : "",
    form?.silkMark ? "Silk mark" : "",
    form?.madeInIndia ? "Made in India" : "",
  ].filter(Boolean);
  const pieceId = clean(form?.productUniqueId);
  const [open, setOpen] = useState(false);
  // Whether this piece's open was already reported (null = not yet, for any piece id, even "").
  const reported = useRef<string | null>(null);
  const panelId = useId();
  // Each piece starts closed; a new piece is a new chance to show interest.
  useEffect(() => {
    setOpen(false);
    reported.current = null;
  }, [pieceId]);
  const teaser = [
    weaver ? `Woven by ${weaver}` : "",
    steps.length ? `${steps.length} step${steps.length === 1 ? "" : "s"}` : "",
    hours ? `${hours} working hours` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && reported.current !== pieceId) {
      reported.current = pieceId;
      onOpen?.();
    }
  };

  return (
    <div className="hkp-piece-story">
      <section className={`hkp-ps-section hkp-ps-story${open ? " is-open" : ""}`} aria-labelledby="hkp-ps-story">
        <h2 id="hkp-ps-story" className="hkp-ps-heading">
          <button
            type="button"
            className="hkp-ps-toggle"
            aria-expanded={open}
            aria-controls={panelId}
            onClick={toggle}
          >
            <span className="hkp-ps-toggle-text">
              <span className="hkp-ps-title">Making story</span>
              {!open ? (
                <span className="hkp-ps-teaser">{teaser || "See how this piece was made"}</span>
              ) : null}
            </span>
            <span className="hkp-ps-toggle-action">
              {open ? "Hide" : "See how it was made"}
              <span className="hkp-ps-caret" aria-hidden="true">
                ▾
              </span>
            </span>
          </button>
        </h2>

        {open ? (
        <div id={panelId} className="hkp-ps-body">
        <p className="hkp-ps-attribution">{attribution}</p>

        {weaver || loom || months || hours ? (
          <div className="hkp-ps-maker">
            <span className="hkp-ps-maker-mark" aria-hidden="true">
              {(weaver || "W").slice(0, 1).toUpperCase()}
            </span>
            <span className="hkp-ps-maker-text">
              {weaver ? <strong>Woven by {weaver}</strong> : <strong>Handwoven</strong>}
              <span>
                {[
                  loom,
                  months ? `${months} month${months === "1" ? "" : "s"} on the loom` : "",
                  hours ? `${hours} working hours` : "",
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
          </div>
        ) : null}

        {marks.length ? (
          <div className="hkp-ps-marks">
            {marks.map((m) => (
              <span key={m} className="hkp-ps-mark">
                ✓ {m}
              </span>
            ))}
          </div>
        ) : null}

        {steps.length ? (
          <ol className="hkp-ps-timeline">
            {steps.map((s, i) => (
              <li key={s.key}>
                <span className="hkp-ps-dot" aria-hidden="true" />
                <strong>{s.title || `Step ${i + 1}`}</strong>
                {s.subtitle ? <p className="hkp-ps-sub">{s.subtitle}</p> : null}
                {s.text ? <p>{s.text}</p> : null}
                {s.artisan ? (
                  <div className="hkp-ps-artisan">
                    {s.artisanPhoto ? (
                      <HKPImage className="hkp-ps-photo" preset="THUMBNAIL" legacyUrl={s.artisanPhoto} alt="" />
                    ) : (
                      <span className="hkp-ps-artisan-mark" aria-hidden="true">
                        {s.artisan.slice(0, 1).toUpperCase()}
                      </span>
                    )}
                    <span>
                      <strong>{s.artisan}</strong>
                      {s.artisanBio ? <span>{s.artisanBio}</span> : null}
                    </span>
                  </div>
                ) : null}
                {s.media.length ? (
                  <div className="hkp-ps-media">
                    {s.media.map((url, mi) => (
                      <HKPImage
                        key={mi}
                        className="hkp-ps-photo"
                        preset="PRODUCT_DETAIL"
                        legacyUrl={url}
                        alt={`${s.title || "Process"} photo ${mi + 1}`}
                      />
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
        ) : (
          <p className="hkp-ps-empty">No making steps recorded for this piece yet.</p>
        )}

        {/* The studio's loom record names its karkhana, so a reseller's client never gets it. */}
        {!hideBrand && pieceId ? <PublicProductionSummary productUniqueId={pieceId} /> : null}
        </div>
        ) : null}
      </section>

      {specs.length ? (
        <section className="hkp-ps-section" aria-labelledby="hkp-ps-details">
          <h2 id="hkp-ps-details">Details</h2>
          <dl className="hkp-ps-specs">
            {specs.map((s) => (
              <div key={s.label}>
                <dt>{s.label}</dt>
                <dd>{s.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
    </div>
  );
};

export default PieceStory;
